// @Version: 1.0.2
import { Component, EventEmitter, Output, forwardRef, Input, OnDestroy, Inject, signal, SimpleChanges, OnChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, Validators, AbstractControl, ValidationErrors, ControlValueAccessor, FormGroup, FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { ErrorMessagesService } from '../../../services/errorMessages/error-messages.service';
import { ValidatorsService } from '../../../services/validators/validators.service';
import { CustomValidatorsDate } from './date-validators';
import { IErrorCodes } from '../../../services/errorMessages/error-messages.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { Moment } from 'moment';
import moment from 'moment';
import { DATEFORMAT, DATEFORMATCALENDER, DATESTARTVIEW, FC_DATE } from './date.model';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateFormatConfig } from './date.config';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { IFieldItem, ITEMTYPE, VALIDATORS } from '../field-item.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-date',
   templateUrl: './date.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemDateComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemDateComponent),
         multi: true
      },
      { 
         provide: MAT_DATE_LOCALE, 
         useValue: 'de' 
      },
      {
         provide: DateAdapter,
         useClass: MomentDateAdapter,
         deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
      },
      { 
         provide: MAT_DATE_FORMATS, 
         useClass: DateFormatConfig 
      },
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemDateComponent implements OnDestroy, OnChanges, Validators, ControlValueAccessor {

   public VALIDATORS = VALIDATORS;
   public FC_DATE = FC_DATE;
   public DATEFORMATCALENDER = DATEFORMATCALENDER; 
   public DATESTARTVIEW = DATESTARTVIEW; 

   public done$$ = signal(false);

   public fgDate: FormGroup = this.fb.group({});

   public hideRequireMaker: boolean = false;
   public dateFormat: DATEFORMATCALENDER = DATEFORMATCALENDER.DATE;

   private fieldItem_: IFieldItem = {} as IFieldItem;
   private errorCodes_: IErrorCodes = {};
   private sub_?: Subscription;
      
   @Input() clearValueIfDisabled: boolean = true;
   
   @Input() 
   get fieldItem (): IFieldItem {
     return this.fieldItem_;
   }
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }

      
   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();

   get value (): Moment | string {
      return this.fgDate.get(FC_DATE.DATE)?.value ?? '';
   }

   get error (): IErrorCodes {
      return this.errorCodes_;
   }

   constructor (@Inject(MAT_DATE_FORMATS) public config: DateFormatConfig, private vDate: CustomValidatorsDate, private fb: FormBuilder, public sErrorMessages: ErrorMessagesService, private sValidators: ValidatorsService) { }
   
   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.errorCodes_ = this.sErrorMessages.setErrorCodes(changes.fieldItem.currentValue.attribute_5 ?? '', TRANSLATE_PATH.FIELD_ITEM_ERROR);
         this.setDateFormat(changes.fieldItem.currentValue);
         this.initFormGroup(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }
   ngOnDestroy (): void {
      this.fieldItem_ = {} as IFieldItem;
      this.errorCodes_ = {} as IErrorCodes;
      this.done$$.set(false);
      this.fgDate = this.fb.group({});
      this.isRequiredMarker.emit(false); 
      this.hideRequireMaker = false;
      this.dateFormat = DATEFORMATCALENDER.DATE;
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgDate.updateValueAndValidity({emitEvent: true, onlySelf: true});
      return this.fgDate.get(FC_DATE.DATE) ? this.fgDate.get(FC_DATE.DATE)!.valid || this.fgDate.get(FC_DATE.DATE)!.disabled ? null : this.fgDate.get(FC_DATE.DATE)!.errors : null;
   }

   writeValue (value: string | undefined | null): void {
      this.fgDate.get(FC_DATE.DATE)?.setValue(value ? moment(value, DATEFORMAT.MOMENT) : undefined);
      if (typeof value == 'string' && value.trim().length > 0) this.setDirtyTouched(); 
      this.onValidationChange();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }
   registerOnTouched (fn: any): void { 
      this.onTouched = fn; 
   }

   registerOnValidatorChange?(fn: () => void): void {
      this.onValidationChange = fn;
   }

   private onTouched () {}

   private onChange (_: string) {}

   onValidationChange: any = () => {}; 

   setDisabledState (isDisabled: boolean | undefined): void { 
      if (this.fgDate.get(FC_DATE.DATE)) isDisabled ? this.setDisabledControls() : this.fgDate.get(FC_DATE.DATE)?.enable();
      this.onValidationChange();
   }


   private initFormGroup (fieldItem: IFieldItem): boolean {
      if (this.sub_) this.sub_.unsubscribe();
      this.config.format = this.DATEFORMATCALENDER ? this.dateFormat : DATEFORMATCALENDER.DATE; 

      this.fgDate = this.fb.group({ [FC_DATE.DATE]: this.fb.control({value: fieldItem.content ? moment(fieldItem.content, DATEFORMAT.MOMENT) : undefined, disabled: false }, {validators: this.sValidators.setValidators(fieldItem, ITEMTYPE.date), updateOn: 'change'}) });

      this.fgDate.get(FC_DATE.DATE)?.addValidators(this.vDate.isValid(this.fgDate, fieldItem, this.config.format));
      this.sub_ = this.fgDate.get(FC_DATE.DATE)?.valueChanges.pipe(
         debounceTime(500)
      ).subscribe(() => {
         if (!this.value && this.fgDate.get(FC_DATE.DATE)?.pristine) return;
         let val = this.value && typeof this.value != "string" ? this.value.format(DATEFORMAT.MOMENT) : '';
         this.onChange(val);
         this.onTouched();
      });

      this.hideRequireMaker = fieldItem.attribute_1 && fieldItem.attribute_1 != '' ? false : true;
      this.isRequiredMarker.emit(fieldItem.required && !this.hideRequireMaker);

      return true;
   }

   private setDateFormat (fieldItem: IFieldItem): void {
      switch (fieldItem.attribute_2) {
         case DATEFORMATCALENDER.DATE: this.dateFormat = DATEFORMATCALENDER.DATE; break;
         case DATEFORMATCALENDER.MONTHYEAR: this.dateFormat = DATEFORMATCALENDER.MONTHYEAR; break; 
         case DATEFORMATCALENDER.YEAR: this.dateFormat = DATEFORMATCALENDER.YEAR; break; 
         default: this.dateFormat = DATEFORMATCALENDER.DATE; break;
      }
   }

   private setDisabledControls (): void {
      this.fgDate.get(FC_DATE.DATE)?.disable();
      if (this.clearValueIfDisabled) this.fgDate.get(FC_DATE.DATE)?.setValue("");
      this.fgDate.get(FC_DATE.DATE)?.markAsTouched();
   }

   public setMonthAndYear (normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>): void {
      this.setDirtyTouched(); 
      const ctrlValue = this.fgDate.get(FC_DATE.DATE)?.value ?? moment();
      ctrlValue.date(1);
      ctrlValue.month(normalizedMonthAndYear.month());
      ctrlValue.year(normalizedMonthAndYear.year());
      this.fgDate.get(FC_DATE.DATE)?.setValue(ctrlValue);
      datepicker.close();
   }

   public setYear (normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Moment>): void {
      this.setDirtyTouched(); 
      const ctrlValue = this.fgDate.get(FC_DATE.DATE)?.value ?? moment();
      ctrlValue.date(1);
      ctrlValue.month(0);
      ctrlValue.year(normalizedMonthAndYear.year());
      this.fgDate.get(FC_DATE.DATE)?.setValue(ctrlValue);

      datepicker.close();
   }

   private setDirtyTouched (): void {
      this.fgDate.get(FC_DATE.DATE)?.markAsDirty(); 
      this.fgDate.get(FC_DATE.DATE)?.markAsTouched();
   }
}
